import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ActionMenuItem, MenuItemList } from './ActionMenu';

const MENU_WIDTH = 240;

/**
 * Right-click menu, anchored to the pointer.
 *
 * Shares its item rendering with ActionMenu so a row's right-click menu and
 * its "…" menu cannot drift apart. Portaled to document.body and positioned
 * fixed, because the tables it is used in have overflow-hidden ancestors.
 */
export default function ContextMenu({
    x,
    y,
    items,
    onClose,
}: {
    x: number;
    y: number;
    items: ActionMenuItem[];
    onClose: () => void;
}) {
    const menuRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: y, left: x });

    // Keep the menu on screen when opened near an edge.
    useLayoutEffect(() => {
        const height = menuRef.current?.offsetHeight ?? 0;
        const left = Math.min(x, window.innerWidth - MENU_WIDTH - 8);
        const top = height && y + height > window.innerHeight ? Math.max(8, y - height) : y;
        setPosition({ top, left: Math.max(8, left) });
    }, [x, y]);

    useEffect(() => {
        // The right-click that opened this menu may still be propagating when
        // these listeners attach, so anything raised in the same moment would
        // close the menu the instant it appeared. Ignore events older than the
        // mount, and do not listen for `contextmenu` at all - a right-click
        // elsewhere fires `mousedown` first, which already closes us.
        const openedAt = performance.now();

        const shouldIgnore = (event: Event) =>
            typeof event.timeStamp === 'number' && event.timeStamp <= openedAt;

        const onPointerDown = (event: MouseEvent) => {
            if (shouldIgnore(event)) return;
            if (menuRef.current?.contains(event.target as Node)) return;
            onClose();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        const onScroll = () => onClose();

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [onClose]);

    return ReactDOM.createPortal(
        <div
            ref={menuRef}
            role="menu"
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                width: MENU_WIDTH,
            }}
            className="z-50 bg-surface border border-border rounded-lg shadow-lg py-1"
        >
            <MenuItemList items={items} onSelected={onClose} />
        </div>,
        document.body
    );
}
